import './styles.scss'
import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { InputText } from 'primereact/inputtext'
import { Toast } from 'primereact/toast'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import DialogUIModal from 'Components/UIComponents/DialogUIModal'
import Validation from 'Utils/Validation'

const AddCountryWiseExportReport = ({ columns, showForm, setShowForm, fileName, setLoading, handleReportDataFunction, country }) => {
  const { t } = useTranslation()
  const toast = useRef(null)
  const [validField, setValidField] = useState(false)
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [inputForm, setInputForm] = useState({
    year: ''
  })
  const [openModal, setOpenModal] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {} })

  useEffect(() => {
    const initialData = columns
      .filter((column) => column.field !== 'actions')
      .reduce((acc, column) => {
        acc[column.field] = ''

        return acc
      }, {})

    setFormData(initialData)
    setErrors({})
    setValidField(false)
  }, [columns, showForm])

  const insertCountryWiseExportReport = async () => {
    setLoading(true)
    handleDismiss()

    try {
      const data = {
        year: inputForm.year,
        fileName: fileName,
        report: [formData]
      }

      const response = await apiAdapterCoffeeWeb.postCountryWiseExportReport(data)

      if (response.data.statusCode === 200) {
        handleReportDataFunction(response)
        setOpenModal({
          ...openModal,
          showModal: true,
          modalType: 'success',
          message: t('REPORT_DATA_ADDED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
        setShowForm(false)
        setInputForm({
          year: ''
        })
      } else {
        setShowForm(false)
        setInputForm({
          year: ''
        })
        toast.current.show({ severity: t('ERROR_TOAST'), summary: response.data.statusMessage, detail: t('PLEASE_ENTER_ANOTHER_DATA') })
      }
    } catch {
      setOpenModal({
        ...openModal,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_ADD_DATA_PLEASE_TRY_AGAIN_LATER'),
        onRetry: insertCountryWiseExportReport,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (event, field) => {
    const isFieldValid = field !== 'month' && field !== 'MONTH'
    const updatedFormData = { ...formData, [field]: event.target.value }

    if (isFieldValid && Validation.numberDotPlusAndMinusValidation(event.target.value)) {
      setFormData(updatedFormData)
    } else if (!isFieldValid) {
      setFormData(updatedFormData)
    }

    setErrors((prev) => ({
      ...prev,
      [field]: false
    }))
  }

  const handleYearChange = (fieldName, value) => {
    setInputForm((previousValue) => ({
      ...previousValue,
      [fieldName]: value
    }))

    setValidField((previousData) => ({
      ...previousData,
      [fieldName]: false
    }))
  }

  const handleBlur = (fieldName) => {
    if (formData[fieldName] === '') {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: true
      }))
    }
  }

  const handleInputBlur = (fieldName) => {
    setValidField((previousData) => ({
      ...previousData,
      [fieldName]: true
    }))
  }

  const closeDIalogModal = () => {
    setShowForm(false)
    setFormData({})
    setErrors({})
    setInputForm({
      year: ''
    })
  }

  const isSubmitDisabled = () => columns.some((column) => column.field && (formData[column.field] === '' || formData[column.field] === null)) || !inputForm.year

  const renderInput = (column, index) => {
    if (column.field === 'actions') return null

    return (
      <div className="form-group" key={column.field}>
        <span className="p-float-label">
          <InputText
            value={formData[column.field]}
            onChange={(event) => handleInputChange(event, column.field)}
            onBlur={() => {
              handleBlur(column.field)
            }}
            className={`input-fields ${errors[column.field] && formData[column.field] === '' ? 'error' : ''}`}
            placeholder={column?.field?.toLowerCase() === 'month' ? 'E.g: January - 2024' : ''}
            data-testid={`input-${column.field}`}
          />
          <label htmlFor={`${column.field}-${index}`} className={`field-label ${errors[column.field] && formData[column.field] === '' ? 'error-label' : ''}`}>
            {column.field.charAt(0).toUpperCase() + column.field.slice(1).toLowerCase()}
            <span className="required-text-field">*</span>
          </label>
        </span>

        {errors[column.field] && formData[column.field] === '' && <small className="error-message">{`${column.field.charAt(0).toUpperCase() + column.field.slice(1).toLowerCase()} ${'Is Required'}`} </small>}
      </div>
    )
  }

  const renderFooter = () => (
    <div className="add-country-export-footer">
      <ButtonComponent type={'cancel'} onClick={closeDIalogModal} />

      <ButtonComponent type={'submit'} disabled={isSubmitDisabled()} onClick={insertCountryWiseExportReport} />
    </div>
  )

  const handleDismiss = () => {
    setOpenModal({ ...openModal, showModal: false })
  }

  const renderCountryWiseExportAddForm = () => (
    <div className="export-country-dynamic-add-form">
      <div className="form-group">
        <span className="p-float-label">
          <InputText
            id="year"
            value={inputForm.year}
            name="year"
            onChange={(event) => {
              handleYearChange('year', event.target.value)
            }}
            onBlur={() => {
              handleInputBlur('year')
            }}
            className={`input-fields ${validField && !inputForm.year ? 'error' : ''}`}
            placeholder="E.g: Year : 2024"
            data-testid="input-year"
          />
          <label htmlFor="year" className={`field-label ${validField && !inputForm.year ? 'error-label' : ''}`}>
            {t('YEAR_FIELD')}
            <span className="required-text-field">*</span>
          </label>
        </span>
        {validField && !inputForm.year && <small className="error-message">{t('YEAR_FIELD_ERROR_MESSAGE')}</small>}
      </div>

      {columns.map(renderInput)}
    </div>
  )

  return (
    <>
      <DialogUIModal visible={showForm} onHide={closeDIalogModal} header={`${t('ADD_COUNTRY_WISE_EXPORT_REPORT')} - ${country?.nicename}`} footer={renderFooter} body={renderCountryWiseExportAddForm} className="country-wise-export-add-modal" />

      <DialogModal {...openModal} onDismiss={handleDismiss} />

      <Toast ref={toast} position="top-right" />
    </>
  )
}

export default Loader(AddCountryWiseExportReport)
